import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetDatasetDataMutation,
  useMutationDatasetMetadata
} from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { useMutateGetWidgetDefaultConfig } from "@/api/widget/useWidgetAPI";
import AdminAreaSelection from "@/components/adminAreaSelection";
import NotificationContent from "@/components/notification/notificationContent";
import Divider from "@/components/ui/Divider";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import { MAX_POI } from "@/constants/dataset";
import { ERROR_MESSAGE, MESSAGE } from "@/constants/message";
import {
  getAdminAreaRequired,
  getAdminAreaVisibility
} from "@/helpers/datasetAttributeHelper";
import { useAttributeFilter } from "@/hooks/attributeFilter/useAttributeFilter";
import { useH3Configuration } from "@/hooks/h3Configuration/useH3Configuration";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDatasetStream } from "@/hooks/useDatasetStream";
import { RootState } from "@/store";
import { toggleUseGlobalFilter } from "@/store/slice/datasetSlice";
import { setDatasetConfigTemp } from "@/store/slice/spatialSlice";
import {
  ESidebarMenuType,
  setLoadingOverlay,
  setSideBarMenuTypePopupDisplay
} from "@/store/slice/uiSlice";
import {
  DatasetData,
  DatasetDetailResponse,
  DatasetFilter,
  DatasetMetadata
} from "@/types/dataset";
import { PostWidgetDefaultConfigResponse } from "@/types/widget";
import { generateDatasetId } from "@/utils/common";
import {
  convertDatasetResponseToFeatureCollection,
  createNewDataset
} from "@/utils/dataset";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import AggregationFilter from "./AggregationFilter";
import { AttributeFilter } from "./AttributeFilter";
import CustomFilterArea from "./CustomFilterArea";
import EmptyState from "./EmptyState";
import Header from "./Header";
import SavedAdmArea from "./SavedAdmArea";
import SavedFiltersList from "./SavedFiltersList";

const TEST_ID_PREFIX = "dataset-explorer-attribute-filter";

type DatasetRightAttrFilterProps = {
  disabled?: boolean;
  selectedDataset: DatasetDetailResponse | null;
  source?: string;
  isSubscribed?: boolean;
  isLoading?: boolean;
  onClose: () => void;
  totalRows?: number;
  datasetData?: DatasetData;
};

const CatchmentErrorMessage = ({ datasetLength = 0 }) => (
  <span className="mb-2 text-xs text-cinnabar">
    {datasetLength === 0
      ? "No data selected. Please select at least one record to proceed."
      : `Selection exceeds the limit (${datasetLength}/${MAX_POI}). Please reduce the number of records to proceed.`}
  </span>
);

const DatasetRightAttrFilter = ({
  disabled = false,
  selectedDataset,
  isSubscribed = true,
  isLoading = false,
  totalRows = 0,
  datasetData: defaultDatasetData,
  onClose
}: DatasetRightAttrFilterProps) => {
  const [isLoadingDataset, setIsLoadingDataset] = useState(false);
  const [datasetData, setDatasetData] = useState<DatasetData | undefined>();
  const [datasetStreamId, setDatasetStreamId] = useState<string | undefined>();
  const [groupByValue, setGroupByValue] = useState<string | undefined>();
  const [defaultWidgetData, setDefaultWidgetData] = useState<
    PostWidgetDefaultConfigResponse | undefined
  >(undefined);
  const shouldApplyGlobalAreaRef = useRef(false);

  const {
    isUseNewDatasetExplorerModal,
    isAdminTreeEnabled,
    isGlobalFilterOverrideEnabled,
    isBVTDataForCatchmentEnabled
  } = useAppFeatures();
  const dispatch = useDispatch();
  const { sideBarMenuTypePopupDisplay, showCatchmentPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );
  const { datasets } = useSelector((state: RootState) => state.dataset);

  const {
    adminAreaSettings: globalAdminArea,
    selectedAreaIds: globalSelectedAreaIds
  } = useSelector((state: RootState) => state.globalFilter);
  const { handleAddDataset } = useDataset();
  const { handleMovePositionToLayer } = useMap();
  const { showLoading, showError, showSuccess } = useLoadingAndToast();
  const { data: userProfileData } = useGetUserProfileAPI();
  const { mutateAsync: getDatasetMetadata } = useMutationDatasetMetadata();

  const handleErrorGetDataset = () => {
    if (showCatchmentPopup) {
      showError(
        <NotificationContent
          title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
          description={ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION}
        />
      );
    } else {
      showError(
        <NotificationContent title={MESSAGE.DATASET_ADD_TITLE_FAILED}>
          <p className="mt-1">
            {MESSAGE.DATASET_ADD_DESCRIPTION_PREFIX}
            <span className="mx-1 font-semibold">{selectedDataset?.name}</span>
            {MESSAGE.DATASET_ADD_DESCRIPTION_FAILED}
          </p>
        </NotificationContent>
      );
    }
  };

  const { mutateAsync: getDatasetDataPost } = useGetDatasetDataMutation({
    timezone: userProfileData?.timezone,
    options: {
      onError: () => {
        handleErrorGetDataset();
      },
      onSuccess: (data) => {
        setDatasetData(data);
      }
    }
  });
  const { h3Configuration, setH3Configuration } = useH3Configuration({
    selectedDataset
  });
  const {
    isGettingAttributeValues,
    attributeValues,
    defaultSelectedValue,
    filterOptions,
    selectedOptions,
    savedFilters,
    showFilters,
    handleAddAttributeFilter,
    getAvailableFilterOptions,
    initialAdminArea,
    isAttributeFilterValid,
    handleSaveFilter,
    handleEditFilter,
    handleDeleteFilter,
    selectAttributeItem,
    handleSearchChange,
    handleSelectFilter,
    handleAdminAreaChange,
    handleBack,
    searchTerm,
    selectedFilter,
    onFilterChange,
    selectedAdminArea,
    setSelectedAdminArea,
    adminAreaValid,
    isAdminAreaSelection,
    isAdminAreaTreeSelectionValid,
    showAdmAreaTree,
    setShowAdmAreaTree,
    selectedAdminAreaIds,
    setSelectedAdminAreaIds,
    handleSaveAdminAreaTreeSelection,
    isLoadingAdminAreaHierarchy,
    resetAdminAreaIds,
    isOverrideAdminArea,
    setIsOverrideAdminArea,
    isFilterMode,
    isAdminTreeMode,
    isDetailMode,
    isUsingGlobalAdminAreaIds,
    groupByAttributeOptions,
    isGlobalFilterAdminAreaSelected,
    isAdminAreaIdsSameAsGlobal,
    handleSyncSelectionGlobalAdminArea,
    getDataFilterAdminArea,
    onSwitchCustomArea,
    shouldLinkToGlobalFilter
  } = useAttributeFilter({
    selectedDataset,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timezone: userProfileData?.timezone as any,
    isAdminTreeEnabled,
    isGlobalFilterOverrideEnabled
  });
  const { showConfirmation } = useConfirmationModal();

  const handleStreamError = () => {
    setIsLoadingDataset(false);
    setDatasetStreamId(undefined);
    handleErrorGetDataset();
  };

  const { mutateAsync: fetchWidgetDefaultConfig } =
    useMutateGetWidgetDefaultConfig();
  useDatasetStream({
    datasetId: datasetStreamId,
    enabled: !!datasetStreamId,
    timezone: userProfileData?.timezone,
    filters: savedFilters,
    h3Configuration,
    group_by: groupByValue,
    onOpen: () => {
      setIsLoadingDataset(true);
      showLoading(
        <NotificationContent description={MESSAGE.DATASET_ADD_LOADING} />
      );
    },
    onStreamComplete: async (data) => {
      if (!selectedDataset) return;
      setIsLoadingDataset(true);
      try {
        const currentDatasetData = data.rawDataset;
        const attributesSchema = data.datasetMetadata;

        if (!currentDatasetData || !attributesSchema) return;
        if (
          showCatchmentPopup &&
          catchmentSettingsTemp?.datasetConfig &&
          (isBVTDataForCatchmentEnabled ||
            currentDatasetData.rows.length <= MAX_POI)
        ) {
          handleAddDatasetShowCatchment(currentDatasetData, attributesSchema);
        } else {
          const newDataset = await handleAddDataset({
            datasetMetadata: selectedDataset,
            datasetData: currentDatasetData,
            savedFilters,
            attributesSchema: attributesSchema.schema,
            h3Configuration,
            group_by: groupByValue,
            index: Object.keys(datasets).length,
            widget: defaultWidgetData?.data
          });
          if (isAdminAreaSelection) {
            dispatch(
              toggleUseGlobalFilter({
                datasetId: newDataset.data.id,
                useGlobalFilter: shouldLinkToGlobalFilter(
                  shouldApplyGlobalAreaRef.current
                )
              })
            );

            if (shouldApplyGlobalAreaRef.current) {
              await getDataFilterAdminArea();
              shouldApplyGlobalAreaRef.current = false;
            }
          }
        }
        if (
          !isBVTDataForCatchmentEnabled &&
          showCatchmentPopup &&
          currentDatasetData.rows.length > MAX_POI
        ) {
          setIsLoadingDataset(false);
          return;
        }
        dispatch(setLoadingOverlay(false));
        onClose();
        if (sideBarMenuTypePopupDisplay !== ESidebarMenuType.SPATIAL) {
          dispatch(setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL));
        }
      } catch {
        return; // catch block only returns here because the error will be handled by the onError above
      }
      // Wait for dataset to render before showing success notification
      showSuccess(
        <NotificationContent title={MESSAGE.DATASET_ADD_TITLE_SUCCESS}>
          <p className="mt-1">
            {MESSAGE.DATASET_ADD_DESCRIPTION_PREFIX}
            <span className="mx-1 font-semibold">{selectedDataset?.name}</span>
            {MESSAGE.DATASET_ADD_DESCRIPTION_SUCCESS}
          </p>
        </NotificationContent>
      );
      setIsLoadingDataset(false);

      setDatasetStreamId(undefined);
    },
    onError: handleStreamError,
    onRetriesExhausted: handleStreamError
  });

  const showAdminAreaSelection = isAdminAreaSelection && isSubscribed;

  const isGlobalFilterAdminAreaSelection =
    isGlobalFilterOverrideEnabled &&
    showAdminAreaSelection &&
    !isGlobalFilterAdminAreaSelected;

  // sync with global admin area filter
  useEffect(() => {
    const isSameCountry =
      selectedDataset?.country_id === globalAdminArea?.lv0?.value;
    if (!showAdminAreaSelection || !isSameCountry) return;
    handleSyncSelectionGlobalAdminArea();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    globalAdminArea,
    filterOptions,
    globalSelectedAreaIds,
    showAdminAreaSelection,
    isLoadingAdminAreaHierarchy,
    selectedDataset
  ]);

  const handleAddDatasetShowCatchment = (
    currentDatasetData: DatasetData,
    attributesSchema: DatasetMetadata
  ) => {
    if (!selectedDataset || !catchmentSettingsTemp?.datasetConfig) return;
    const { id, nextIndex } = generateDatasetId(
      selectedDataset.id,
      catchmentSettingsTemp?.datasetConfig?.datasetIndices
    );
    const datasetName =
      nextIndex > 1
        ? `${selectedDataset.name} (${nextIndex - 1})`
        : selectedDataset.name;
    const newDataset = createNewDataset({
      dataset: selectedDataset,
      datasetId: id,
      datasetName: datasetName,
      attributesSchema: attributesSchema.schema
    });
    const featureCollection =
      convertDatasetResponseToFeatureCollection(currentDatasetData);
    handleMovePositionToLayer(featureCollection);
    dispatch(
      setDatasetConfigTemp({
        uniqueId: id,
        originalId: selectedDataset.id,
        dataset: newDataset,
        rawDataset: currentDatasetData,
        filters: savedFilters,
        nextIndex
      })
    );
  };

  const processAddDataSet = async (shouldApplyGlobal = false) => {
    if (!selectedDataset?.id) return;
    if (!isBVTDataForCatchmentEnabled && (datasetData || defaultDatasetData)) {
      const attributesSchema = await getDatasetMetadata({
        id: selectedDataset.id,
        h3Configuration
      });
      const currentDatasetData = datasetData ?? defaultDatasetData;
      if (
        showCatchmentPopup &&
        currentDatasetData &&
        currentDatasetData.rows.length <= MAX_POI &&
        catchmentSettingsTemp?.datasetConfig
      ) {
        handleAddDatasetShowCatchment(currentDatasetData, attributesSchema);
        onClose();
        return;
      }
    }
    /** Only fetch default widget config when adding dataset from direct
     * dataset explorer (not from catchment popup) — the catchment add path
     * doesn't use the widget config.
     */
    if (!showCatchmentPopup) {
      try {
        // only fetch default widget config for BVT dataset as it's the only dataset with default widget config currently.
        // This is to prevent unnecessary API call for other datasets that don't have default widget config.
        if (selectedDataset.source === "bvt") {
          const defaultWidget = await fetchWidgetDefaultConfig({
            id: selectedDataset.id,
            grid_config: {
              aggregation_type: h3Configuration?.aggregationType ?? ""
            },
            group_by: groupByValue ?? ""
          });
          setDefaultWidgetData(defaultWidget ?? undefined);
        }
      } catch {
        // Default widget config is optional — proceed with adding the dataset
        // even if it fails so the user isn't blocked from loading the data.
        setDefaultWidgetData(undefined);
      }
    }
    shouldApplyGlobalAreaRef.current = shouldApplyGlobal;
    setDatasetStreamId(selectedDataset.id);
  };

  const onAddDataset = async () => {
    if (isGlobalFilterAdminAreaSelection) {
      showConfirmation({
        title: "Confirmation",
        message: MESSAGE.DATASET_CONFIRMATION_CHANGE_GLOBAL_FILTER,
        confirmLabel: "Yes",
        cancelLabel: "No",
        onConfirm: () => onConfirmation(true),
        onCancel: () => onConfirmation(false)
      });
      return;
    }
    await processAddDataSet();
  };

  const onConfirmation = async (value: boolean) => {
    await processAddDataSet(value);
  };

  const onSaveFilter = async () => {
    const newFilter = await handleSaveFilter();
    if (
      !isBVTDataForCatchmentEnabled &&
      showCatchmentPopup &&
      selectedDataset
    ) {
      await getDatasetDataPost({
        id: selectedDataset.id,
        filter: newFilter,
        h3Configuration
      });
    }
  };

  const onDeleteFilter = async (filter: DatasetFilter) => {
    const filteredSavedFilter = handleDeleteFilter(filter);
    if (
      !isBVTDataForCatchmentEnabled &&
      selectedDataset &&
      showCatchmentPopup
    ) {
      await getDatasetDataPost({
        id: selectedDataset.id,
        filter: filteredSavedFilter,
        h3Configuration
      });
    }
  };

  const disabledFilter = disabled || !isSubscribed;
  const showAggregationSelection = selectedDataset && isSubscribed;

  const totalRowsDataset = useMemo(
    () => datasetData?.rows?.length ?? totalRows,
    [datasetData?.rows?.length, totalRows]
  );

  const isInvalidPOILength = useMemo(
    () =>
      !isBVTDataForCatchmentEnabled &&
      !!showCatchmentPopup &&
      (totalRowsDataset === 0 || totalRowsDataset > MAX_POI),
    [isBVTDataForCatchmentEnabled, showCatchmentPopup, totalRowsDataset]
  );

  const showErrorPOICondition = useMemo(
    () => showCatchmentPopup && selectedDataset && isInvalidPOILength,
    [showCatchmentPopup, selectedDataset, isInvalidPOILength]
  );

  const adminTreeConfig = {
    className: "flex-1 min-h-0 max-h-none",
    enabled: isAdminTreeEnabled,
    selectedIds: selectedAdminAreaIds,
    setSelectedIds: (ids: string[]) => {
      setSelectedAdminAreaIds(ids);
    }
  };

  const shouldShowSaveButton =
    (showFilters && selectedFilter) || isAdminTreeMode;

  const isSaveDisabled = isFilterMode
    ? !isAttributeFilterValid
    : !isAdminAreaTreeSelectionValid || isUsingGlobalAdminAreaIds;

  const handleSave = isFilterMode
    ? onSaveFilter
    : () => {
        handleSaveAdminAreaTreeSelection();
        setIsOverrideAdminArea(
          isGlobalFilterAdminAreaSelected && !isAdminAreaIdsSameAsGlobal
        );
      };

  return (
    <div className="flex flex-col w-[20%] h-full">
      <div className="flex flex-col h-[calc(100%-40px)] bg-white-smoke rounded-[12px]">
        <Header
          showBackButton={showFilters || showAdmAreaTree}
          onBack={() => {
            handleBack();
            if (isAdminTreeMode) resetAdminAreaIds();
          }}
          title={showAdmAreaTree ? "Administrative Area" : "Attribute Filter"}
        />

        <div
          className={twMerge(
            "flex flex-col flex-1 min-h-0 px-3 mt-1",
            showAdmAreaTree ? "pt-0" : "pt-3"
          )}
        >
          {isFilterMode && (
            <AttributeFilter
              savedFilters={savedFilters}
              getAvailableFilterOptions={getAvailableFilterOptions}
              handleSelectFilter={handleSelectFilter}
              selectAttributeItem={selectAttributeItem}
              defaultSelectedValue={defaultSelectedValue}
              attributeValues={attributeValues}
              selectedOptions={selectedOptions}
              searchTerm={searchTerm}
              isGettingAttributeValues={isGettingAttributeValues}
              onSearchChange={handleSearchChange}
              onChange={onFilterChange}
            />
          )}
          {isAdminTreeMode && (
            <AdminAreaSelection
              selectedData={selectedAdminArea || initialAdminArea}
              onSelectData={() => {}}
              options={{
                showCountryLevel: false,
                showTitle: false
              }}
              treeConfig={adminTreeConfig}
            />
          )}
          {isDetailMode && (
            <>
              <p className="text-[10px] text-txtBody mb-3">
                You may add filters to the attributes based on the dataset you
                select.
              </p>
              {isAdminTreeEnabled && !selectedDataset && (
                <EmptyState className="flex-grow-0 gap-2" />
              )}
              {isGlobalFilterOverrideEnabled && showAdminAreaSelection && (
                <CustomFilterArea
                  disabled={!isGlobalFilterAdminAreaSelected}
                  checked={isOverrideAdminArea}
                  onSwitchChange={onSwitchCustomArea}
                />
              )}
              {isAdminTreeEnabled &&
                showAdminAreaSelection &&
                selectedAdminAreaIds.length > 0 && (
                  <LoadingWrapper
                    isLoading={isLoadingAdminAreaHierarchy}
                    loadingClassName="h-16"
                  >
                    <SavedAdmArea
                      filter={savedFilters.find(
                        (f) => f.attributeCode === "adm_area_level_1"
                      )}
                      onEdit={() => {
                        setShowAdmAreaTree(true);
                      }}
                      onDelete={() => {
                        setSelectedAdminAreaIds([]);
                      }}
                      disabled={
                        isGlobalFilterAdminAreaSelected && !isOverrideAdminArea
                      }
                    />
                  </LoadingWrapper>
                )}
              {isAdminTreeEnabled && <Divider className="my-3" />}
              {!isAdminTreeEnabled && showAdminAreaSelection && (
                <AdminAreaSelection
                  disabled={
                    isGlobalFilterAdminAreaSelected && !isOverrideAdminArea
                  }
                  selectedData={selectedAdminArea || initialAdminArea}
                  onSelectData={(newAdminArea) => {
                    setSelectedAdminArea(newAdminArea);
                    handleAdminAreaChange(newAdminArea);
                  }}
                  options={{
                    showCountryLevel: false,
                    showLabelAboveInput: false,
                    showLabel: true,
                    prefixLabel: "Select",
                    required: getAdminAreaRequired(selectedDataset),
                    visible: getAdminAreaVisibility(selectedDataset)
                  }}
                />
              )}
              {isAdminTreeEnabled &&
                showAdminAreaSelection &&
                selectedAdminAreaIds.length === 0 && (
                  <Button
                    variant="secondary"
                    className="btn-secondary px-4 font-semibold"
                    onClick={() => {
                      setShowAdmAreaTree(true);
                    }}
                  >
                    Adm. Area
                  </Button>
                )}
              <div className="flex flex-col flex-1 overflow-auto">
                {showAggregationSelection && (
                  <AggregationFilter
                    onChangeAggregationType={(value) => {
                      setH3Configuration((prev) => ({
                        ...prev,
                        aggregationType: value
                      }));
                    }}
                    onChangeGroupBy={(value) => {
                      setGroupByValue(value);
                    }}
                    value={h3Configuration}
                    groupByValue={groupByValue}
                    spatialType={selectedDataset?.spatial_aggregation_type}
                    dataSource={selectedDataset?.source}
                    openFrom="datasetModal"
                    groupByAttributeOptions={groupByAttributeOptions}
                  />
                )}
                {!isUseNewDatasetExplorerModal && !showAggregationSelection && (
                  <EmptyState />
                )}
                {!isUseNewDatasetExplorerModal && <Divider className="my-3" />}
                {showErrorPOICondition && !isLoading && (
                  <CatchmentErrorMessage datasetLength={totalRowsDataset} />
                )}
                <Button
                  variant="secondary"
                  disabled={
                    disabledFilter ||
                    savedFilters.length === filterOptions.length ||
                    !adminAreaValid
                  }
                  className={twMerge(
                    "h-[40px] w-full border-teal-500 bg-white text-teal-500  border text-sm font-bold disabled:border-gainsboro disabled:border disabled:text-gainsboro mb-3",
                    isUseNewDatasetExplorerModal && showAdminAreaSelection
                      ? "mt-3"
                      : ""
                  )}
                  onClick={handleAddAttributeFilter}
                >
                  {!isAdminTreeEnabled && "Add"} Attribute Filter
                </Button>
                {!isAdminTreeEnabled &&
                  isUseNewDatasetExplorerModal &&
                  !showAggregationSelection && <EmptyState />}
                {/* create saved admin tree */}
                {savedFilters.length > 0 && (
                  <SavedFiltersList
                    filterOptions={filterOptions}
                    filters={savedFilters}
                    onEdit={handleEditFilter}
                    onDelete={onDeleteFilter}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="mt-4">
        {shouldShowSaveButton ? (
          <Button
            variant="secondary"
            disabled={isSaveDisabled}
            className="w-full h-auto py-2 text-sm font-bold text-teal-500 bg-white border-teal-500 disabled:bg-white disabled:text-gainsboro disabled:border disabled:border-gainsboro"
            onClick={handleSave}
            data-testid={`${TEST_ID_PREFIX}-save-button`}
          >
            Save
          </Button>
        ) : (
          !showFilters &&
          !showAdmAreaTree && (
            <Button
              disabled={
                disabledFilter ||
                isLoadingDataset ||
                !adminAreaValid ||
                isInvalidPOILength ||
                isLoadingAdminAreaHierarchy
              }
              className="w-full h-[40px] py-2 border-teal-500 bg-teal-500 text-white text-sm font-bold disabled:bg-gainsboro disabled:border-none flex-none"
              onClick={onAddDataset}
              data-testid={`${TEST_ID_PREFIX}-add-dataset-button`}
            >
              Add Dataset
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default DatasetRightAttrFilter;
